import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCreateIssuesOfRfqRequest } from "../../../../__generated__/orval/endpoints/issues-of-rfq-requests/issues-of-rfq-requests";
import { IssuesOfRfqForm } from "./-internal-components/IssuesOfRfqForm";
import {
  type IssuesOfRfqSchema,
  issuesOfRfqSchema,
} from "./-schemas/issuesOfRfqSchema";

const toIsoOrUndefined = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const toPreviousTicketId = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const id = Number(value);

  return Number.isFinite(id) ? id : undefined;
};

const toMatnumbers = (
  values?: Array<string | { matnumber: string }>,
): string[] | undefined => {
  if (!values?.length) {
    return undefined;
  }

  const matnumbers = values
    .map((value) => (typeof value === "string" ? value : value.matnumber))
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return matnumbers.length ? matnumbers : undefined;
};

const IssuesOfRfqRequest: React.FC = () => {
  const { t } = useTranslation();

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      backup_requester: null,
      closing_date: "",
      description: "",
      discipline: null,
      file_attachments: [],
      identified_problem_type: null,
      m5: "",
      matnumber: [],
      opening_date: "",
      previous_ticket: "",
      purchase_order: "",
      purchase_requisition: "",
      requester_field: null,
      ritm: "",
      service_now_reason: null,
      step_purchase: null,
      subject: "",
      technical_contact: "",
      value_current: "",
      value_new: "",
      vendor: null,
      work_order_number: "",
      work_order_responsible_person: null,
    },
    resolver: zodResolver(issuesOfRfqSchema),
  });

  const { mutate, isPending } = useCreateIssuesOfRfqRequest({
    mutation: {
      onError: () => {
        enqueueSnackbar(t("mmRequestChild.errorMessage"), {
          variant: "error",
        });
      },
      onSuccess: (response) => {
        enqueueSnackbar(t("mmRequestChild.successMessage"), {
          variant: "success",
        });
        window.location.href = `/vbuyer/mm_requests?flash_message=${t("mmRequestChild.flashMessage")}${response.mm_request?.id}`;
      },
    },
  });

  const handleFilesChange = useCallback(
    (fileAttachments: string[]) => {
      setValue("file_attachments", fileAttachments);
    },
    [setValue],
  );

  const onSubmit: SubmitHandler<IssuesOfRfqSchema> = useCallback(
    (data) => {
      const requestData: unknown = {
        issues_of_rfq_request: {
          closing_date: toIsoOrUndefined(data.closing_date),
          discipline: data.discipline ?? undefined,
          identified_problem_type_id: data.identified_problem_type?.id,
          m5: data.m5 ? Number(data.m5) : undefined,
          mm_request_attributes: {
            backup_requester_id: data.backup_requester?.id,
            description: data.description,
            file_attachments: data.file_attachments,
            matnumber: toMatnumbers(data.matnumber),
            previous_ticket_id: toPreviousTicketId(data.previous_ticket),
            requester_field: data.requester_field?.name,
            subject: data.subject,
          },
          opening_date: toIsoOrUndefined(data.opening_date),
          purchase_order: data.purchase_order || undefined,
          purchase_requisition: data.purchase_requisition || undefined,
          ritm: data.ritm || undefined,
          service_now_reason: data.service_now_reason ?? undefined,
          step_purchase: data.step_purchase ?? undefined,
          technical_contact: data.technical_contact || undefined,
          value_current: data.value_current || undefined,
          value_new: data.value_new || undefined,
          vendor_id: data.vendor?.id,
          work_order_number: data.work_order_number || undefined,
          work_order_responsible_person_id:
            data.work_order_responsible_person?.id,
        },
      };

      mutate({
        data: requestData as unknown as Parameters<typeof mutate>[0]["data"],
      });
    },
    [mutate],
  );

  return (
    <PageContainer
      backTo="/vbuyer/mm_requests/create_new_requests"
      maxWidth="md"
      showBackButton
      title={t("common.issuesOfRfq")}
    >
      <AppCard>
        <IssuesOfRfqForm
          control={control}
          onFilesChange={handleFilesChange}
          onSubmit={handleSubmit(onSubmit)}
          setValue={setValue}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="success"
              loading={isPending}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </IssuesOfRfqForm>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/issues_of_rfq_requests/new",
)({
  component: IssuesOfRfqRequest,
});
